// SaaS Uygulaması için Type Definitions

export interface User {
  id: string;
  phoneNumber?: string; // Desktop için opsiyonel
  email?: string;
  name?: string;
  avatar?: string;
  role: UserRole;
  teamId?: string;
  teamRole?: TeamRole | null;
  deviceId: string;
  deviceInfo: DeviceInfo;
  subscription: Subscription;
  permissions: UserPermissions;
  createdAt: Date;
  updatedAt: Date;
  lastLoginAt: Date | null;
  isActive: boolean;
  membershipType: MembershipType; // Yeni alan
  desktopCode?: string; // Desktop için özel kod
}

export interface Team {
  id: string;
  name: string;
  description?: string;
  ownerId: string;
  members: TeamMember[];
  settings: TeamSettings;
  subscription: TeamSubscription;
  createdAt: Date;
  updatedAt: Date;
  isActive: boolean;
  inviteCode: string; // Ekip davet kodu
}

export interface TeamMember {
  userId: string;
  role: TeamRole;
  permissions: TeamMemberPermissions;
  invitedAt: Date;
  joinedAt?: Date;
  isActive: boolean;
  invitedBy: string;
}

export interface DeviceInfo {
  deviceId: string;
  platform: 'ios' | 'android' | 'web' | 'desktop';
  deviceName: string;
  osVersion: string;
  appVersion: string;
  fingerprint: string;
}

export interface Subscription {
  id: string;
  userId: string;
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  startDate: Date | any; // Firestore Timestamp
  endDate: Date | any; // Firestore Timestamp
  autoRenew: boolean;
  paymentMethod?: PaymentMethod;
  trialEndsAt?: Date | any; // Firestore Timestamp
  isTrial: boolean;
  createdAt: Date | any; // Firestore Timestamp
}

export interface TeamSubscription {
  id: string;
  teamId: string;
  plan: TeamSubscriptionPlan;
  status: SubscriptionStatus;
  startDate: Date | any; // Firestore Timestamp
  endDate: Date | any; // Firestore Timestamp
  autoRenew: boolean;
  paymentMethod?: PaymentMethod;
  memberLimit: number;
  currentMembers: number;
  createdAt: Date | any; // Firestore Timestamp
}

export interface PaymentMethod {
  type: 'google_play' | 'app_store' | 'cash' | 'bank_transfer';
  details?: any;
  lastUsed: Date;
}

export interface UserPermissions {
  canViewJobs: boolean;
  canAddJobs: boolean;
  canEditJobs: boolean;
  canDeleteJobs: boolean;
  canViewCustomers: boolean;
  canViewPrices: boolean;
  canViewReports: boolean;
  canManageTeam: boolean;
  canViewInvoices: boolean;
  canViewPayments: boolean;
}

export interface TeamMemberPermissions {
  canViewJobs: boolean;
  canAddJobs: boolean;
  canEditJobs: boolean;
  canDeleteJobs: boolean;
  canViewCustomers: boolean;
  canViewPrices: boolean;
  canViewReports: boolean;
  canViewInvoices: boolean;
  canViewPayments: boolean;
  canViewCustomerPrices: boolean; // Müşteri fiyatlarını görebilir mi?
}

export interface TeamSettings {
  allowMemberInvites: boolean;
  requireApprovalForInvites: boolean;
  defaultMemberPermissions: TeamMemberPermissions;
  allowMemberToLeave: boolean;
  allowOwnerToRemoveMembers: boolean;
  dataRetentionDays: number;
  backupEnabled: boolean;
}

export interface Invitation {
  id: string;
  teamId: string;
  invitedPhoneNumber: string;
  invitedBy: string;
  role: TeamRole;
  permissions: TeamMemberPermissions;
  status: InvitationStatus;
  expiresAt: Date | any; // Firestore Timestamp
  createdAt: Date | any; // Firestore Timestamp
  acceptedAt?: Date | any; // Firestore Timestamp
  rejectedAt?: Date | any; // Firestore Timestamp
}

export interface AuthSession {
  id: string;
  userId: string;
  deviceId: string;
  token: string;
  refreshToken: string;
  expiresAt: Date | any; // Firestore Timestamp
  createdAt: Date | any; // Firestore Timestamp
  lastActivityAt: Date | any; // Firestore Timestamp
}

export interface AuditLog {
  id: string;
  userId: string;
  teamId?: string;
  action: string;
  resource: string;
  resourceId: string;
  details: any;
  ipAddress: string;
  userAgent: string;
  createdAt: Date;
}

// Enums
export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  USER = 'user'
}

export enum TeamRole {
  OWNER = 'owner',
  ADMIN = 'admin',
  MEMBER = 'member',
  VIEWER = 'viewer'
}

export enum SubscriptionPlan {
  FREE = 'free',
  PAID = 'paid'
}

export enum TeamSubscriptionPlan {
  FREE = 'free',
  PAID = 'paid'
}

export enum SubscriptionStatus {
  ACTIVE = 'active',
  EXPIRED = 'expired',
  CANCELLED = 'cancelled',
  SUSPENDED = 'suspended',
  TRIAL = 'trial'
}

export enum InvitationStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  EXPIRED = 'expired'
}

export enum MembershipType {
  MOBILE = 'mobile', // Telefon numarası ile
  DESKTOP = 'desktop' // Desktop kodu ile
}

// API Response Types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Auth Types
export interface LoginRequest {
  phoneNumber?: string; // Desktop için opsiyonel
  deviceInfo: DeviceInfo;
  membershipType: MembershipType;
  desktopCode?: string; // Desktop login için
}

export interface RegisterRequest {
  phoneNumber?: string; // Desktop için opsiyonel
  name?: string;
  email?: string;
  deviceInfo: DeviceInfo;
  teamCode?: string; // Ekip daveti için
  membershipType: MembershipType;
  desktopCode?: string; // Desktop üyelik için
}

export interface AuthResponse {
  user: User;
  session: AuthSession;
  team?: Team;
}

// Team Management Types
export interface CreateTeamRequest {
  name: string;
  description?: string;
}

export interface InviteMemberRequest {
  phoneNumber: string;
  role: TeamRole;
  permissions: TeamMemberPermissions;
}

export interface UpdateMemberPermissionsRequest {
  userId: string;
  permissions: TeamMemberPermissions;
}

// Subscription Types
export interface CreateSubscriptionRequest {
  plan: SubscriptionPlan;
  paymentMethod: PaymentMethod;
  teamId?: string;
}

export interface ExtendSubscriptionRequest {
  duration: number; // gün cinsinden
  paymentMethod: PaymentMethod;
}

// Device Management Types
export interface DeviceRegistrationRequest {
  deviceInfo: DeviceInfo;
  userId: string;
}

export interface DeviceValidationResponse {
  isValid: boolean;
  reason?: string;
  requiresReauth: boolean;
}
